'use client';
import { useEffect } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';

declare global {
  interface Window {
    gtag?: (
      command: string,
      targetId: string | Date,
      config?: Record<string, any>
    ) => void;
    dataLayer?: any[];
  }
}

export function useGoogleAnalytics() {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  useEffect(() => {
    if (process.env.NODE_ENV !== 'production') return;

    const measurementId = process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID;
    if (!measurementId) return;

    if (!window.dataLayer) {
      const script = document.createElement('script');
      script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
      script.async = true;
      document.head.appendChild(script);

      window.dataLayer = [];
      window.gtag = function() { 
        window.dataLayer?.push(arguments); 
      };
      window.gtag('js', new Date());
      window.gtag('config', measurementId);
    }

    const url = pathname + (searchParams.toString() ? `?${searchParams}` : '');
    window.gtag?.('event', 'page_view', { page_path: url });
  }, [pathname, searchParams]);
}

export function trackEvent(eventName: string, eventParams: Record<string, any> = {}) {
  if (process.env.NODE_ENV === 'production' && window.gtag) {
    window.gtag('event', eventName, eventParams);
  }
}

// Conversion tracking helpers
export function trackFormSubmit(formName: string, formData?: Record<string, any>) {
  trackEvent('form_submit', {
    form_name: formName,
    ...formData
  });
}

export function trackButtonClick(buttonId: string, buttonLabel?: string) {
  trackEvent('button_click', {
    button_id: buttonId,
    button_label: buttonLabel
  });
}

export function trackProjectView(projectTitle: string, projectCategory: string) {
  trackEvent('project_view', {
    project_title: projectTitle,
    project_category: projectCategory
  });
}

export function trackServiceView(serviceName: string) {
  trackEvent('service_view', {
    service_name: serviceName
  });
}

export function trackNavigation(fromPage: string, toPage: string) {
  trackEvent('navigation', {
    from_page: fromPage,
    to_page: toPage
  });
}